import { ajvResolver } from '@hookform/resolvers/ajv';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { cloneDeep } from 'lodash';
import { Controller, Path, SubmitHandler, useForm } from 'react-hook-form';
import { SidebarAccordion } from 'src/components/accordion';
import { useEditComponentPageContext } from 'src/components/context/components/EditComponentPageContext';
import { Drawer } from 'src/components/drawer';
import { SidebarHeader } from 'src/components/header';
import {
  NumberInputWidget,
  SelectDropdownWidget,
  SwitchButtonWidget,
} from 'src/components/widget';
import { ResourceSelectorSidebar } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget';
import { ResourceType } from 'src/components/widget/resourceSelectorWidget/resourceSelectorSidebarWidget/ResourceSelectorSidebar.interface';
import { useGetThemes } from 'src/hooks';
import { IGcfComponentConfig } from 'src/interfaces';
import { lang } from 'src/utils';
import { handleComponentConfigChange } from 'src/utils/changeHandler';

import { InfoBox } from '../../modules/InfoBox';
import { useThemeOptions } from '../FormSelector.hook';
import { CommonFormProps } from '../FormSelector.interface';
import { FormWrapper } from '../FormWrapper';
import { ListingWithOptionalVisualsColumnStyle } from '../listingWithOptionalVisuals/ListingWithOptionalVisuals.interface';
import { HeaderTextStylingAccordion } from '../modules/headerTextStylingAccordion/HeaderTextStylingAccordion';
import { HeaderTextStylingOptions } from '../modules/headerTextStylingAccordion/HeaderTextStylingAccordion.interface';
import { ProductsDrawer } from '../modules/productsDrawer';
import {
  ListingVisualsGalleryConfigInterface,
  ListingVisualsGalleryConfigSchema,
} from './ListingVisualGallery.interface';
import {
  SidebarContainer,
  SidebarContent,
} from './ListingVisualGallery.styled';
import { useLOVForm } from './ListingVisualGalleryForm.hook';

export const ListingVisualGalleryForm = (props: CommonFormProps) => {
  const {
    control,
    watch,
    handleSubmit: submitWrapper,
    reset,
    formState,
    getValues,
  } = useForm<IGcfComponentConfig>({
    values: cloneDeep(props.componentDetails.config),
    resolver: ajvResolver(
      ListingVisualsGalleryConfigSchema as unknown as JSONSchemaType<ListingVisualsGalleryConfigInterface>,
    ),
  });

  const handleOnChange = (
    key: Path<IGcfComponentConfig> | Path<IGcfComponentConfig>[],
    value: unknown,
    fieldOnChange?: any,
  ) => {
    props.onChange(props.componentDetails.config ?? {}, key, value);
    fieldOnChange && fieldOnChange(value);
  };

  const handleMultipleChange = (
    keys: string[],
    value: unknown[],
    fieldOnChange?: (...event: any[]) => void,
  ) => {
    props.onChange(props.componentDetails.config ?? {}, keys, value);
    if (fieldOnChange) {
      Array.isArray(value) ? fieldOnChange(value[0]) : fieldOnChange(value);
    }
  };

  const {
    openBackgroundDrawer,
    openTitleDrawer,
    openProductsDrawer,
    onToggleTitleDrawer,
    onToggleBackgroundDrawer,
    onToggleProductsDrawer,
    openSettingsDrawer,
    onToggleSettingsDrawer,
  } = useLOVForm();

  const { data: themes = [] } = useGetThemes();
  const { handleChangeComponentDetails } = useEditComponentPageContext();
  const themeOptions = useThemeOptions();

  const handleSubmit: SubmitHandler<IGcfComponentConfig> = (
    value: typeof props.componentDetails.config,
  ) => {
    props.onSave(value);
  };

  const headerTextStyleHasErrors = () => {
    if (
      formState.errors.header ||
      formState.errors.disclaimer ||
      formState.errors.productList?.config?.sizeLabel ||
      formState.errors.productList?.config?.sizes
    )
      return true;
    else return false;
  };

  const settingsHasErrors = () => {
    if (
      formState.errors.lineupColumns ||
      formState.errors.listingHeight ||
      formState.errors.columnStyle
    )
      return true;
    else return false;
  };

  const handleThemeChange = (themeId: unknown) => {
    const theme = themes.find((theme) => theme.id === themeId);

    if (theme) {
      handleChangeComponentDetails([themeId, theme], ['theme_id', 'theme']);
    }
  };
  const hasBackgroundImage = (mop?: { background?: string }) =>
    typeof mop?.background === 'string' && mop.background.trim() !== '';

  const items = [
    {
      label: lang.componentSettings,
      onLabelClick: onToggleSettingsDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: settingsHasErrors(),
    },
    {
      label: lang.background,
      onLabelClick: onToggleBackgroundDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: props.hasTriedSaving
        ? !hasBackgroundImage(props.componentDetails.config)
        : false,
    },
    {
      label: lang.headerTextStyling,
      onLabelClick: onToggleTitleDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: headerTextStyleHasErrors(),
    },
    {
      label: lang.products,
      onLabelClick: onToggleProductsDrawer,
      content: <div />,
      chevronIcon: <ArrowForwardIcon />,
      disabledExpand: true,
      hasError: false,
    },
  ];

  const showNumberofColumns = watch('columnStyle') === 'continuous';

  return (
    <FormWrapper
      form={props.form}
      formState={formState}
      formReset={reset}
      formGetValues={getValues}
      onSave={submitWrapper(handleSubmit)}>
      <SidebarAccordion items={items} />
      <Controller
        name="background"
        control={control}
        render={({ field, fieldState }) => (
          <ResourceSelectorSidebar
            label="Background"
            type={ResourceType.BACKGROUND}
            defaultAssetName={field.value}
            openResourceSelectorDrawer={openBackgroundDrawer}
            onToggleResourceSelectorDrawer={onToggleBackgroundDrawer}
            onChange={(value) => {
              handleMultipleChange(['background'], [value]);
            }}
            hasError={!!fieldState.error}
            error={fieldState.error?.message || false}
          />
        )}
      />
      <Drawer open={openTitleDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.headerTextStyling}
            onBackClick={onToggleTitleDrawer}
          />
          <Grid container spacing={2} style={{ padding: '26px' }}>
            <Grid item xs={12}>
              <SelectDropdownWidget
                label={lang.selectFontFormatting}
                selectOptions={themeOptions}
                value={props.componentDetails?.theme_id}
                onChange={handleThemeChange}
              />
            </Grid>
          </Grid>
          <SidebarContent>
            <HeaderTextStylingAccordion
              componentConfig={props.componentDetails.config}
              handleOnChange={handleOnChange}
              errors={formState?.errors}
              theme={props.componentDetails.theme}
              hidden={[
                HeaderTextStylingOptions.HEADER_LAYOUT,
                HeaderTextStylingOptions.MOLECULE_DISPLAY_LABEL,
              ]}
            />
          </SidebarContent>
        </SidebarContainer>
      </Drawer>

      <Drawer open={openSettingsDrawer}>
        <SidebarContainer>
          <SidebarHeader
            label={lang.componentSettings}
            onBackClick={onToggleSettingsDrawer}
          />
          <SidebarContent>
            <Grid container spacing={2} style={{ padding: '26px' }}>
              <Grid item xs={12}>
                <Controller
                  name="isFlavourCycling"
                  control={control}
                  defaultValue={false}
                  render={({ field: { ref, ...field } }) => (
                    <SwitchButtonWidget
                      {...field}
                      label="Display flavor cycling"
                      ref={ref}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'isFlavourCycling',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="showComponentProductDisclaimer"
                  control={control}
                  defaultValue={false}
                  render={({ field: { ref, ...field } }) => (
                    <SwitchButtonWidget
                      {...field}
                      label="Show product disclaimers"
                      ref={ref}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'showComponentProductDisclaimer',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
              {showNumberofColumns && (
                <Grid item xs={12}>
                  <Controller
                    name="lineupColumns"
                    control={control}
                    render={({ field: { ref, ...field }, fieldState }) => (
                      <NumberInputWidget
                        {...field}
                        ref={ref}
                        label="Number of columns"
                        value={field.value || ''}
                        onChange={(value) => {
                          if ((value as number) >= 1 && value) {
                            handleComponentConfigChange(
                              props,
                              `lineupColumns`,
                              value,
                              field.onChange,
                            );
                          }
                        }}
                        hasError={!!fieldState.error}
                        error={fieldState.error?.message || false}
                        options={{
                          min: 1,
                        }}
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <SelectDropdownWidget
                  label={lang.selectTheme}
                  selectOptions={themeOptions}
                  value={props.componentDetails?.theme_id}
                  onChange={handleThemeChange}
                />
                <InfoBox>{lang.changeThemeInfoText}</InfoBox>
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="columnStyle"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <SelectDropdownWidget
                      {...field}
                      ref={ref}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      label="Column style"
                      selectOptions={[
                        {
                          label: 'Category',
                          value: ListingWithOptionalVisualsColumnStyle.category,
                        },
                        {
                          label: 'Continuous',
                          value:
                            ListingWithOptionalVisualsColumnStyle.continuous,
                        },
                      ]}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'columnStyle',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="listingHeight"
                  control={control}
                  render={({ field: { ref, ...field }, fieldState }) => (
                    <NumberInputWidget
                      {...field}
                      ref={ref}
                      label="Listing height"
                      value={field.value || ''}
                      onChange={(value) => {
                        if (
                          (value as number) >= 1 &&
                          (value as number) <= 12 &&
                          value
                        ) {
                          handleComponentConfigChange(
                            props,
                            'listingHeight',
                            value,
                            field.onChange,
                          );
                        }
                      }}
                      hasError={!!fieldState.error}
                      error={fieldState.error?.message || false}
                      options={{
                        min: 1,
                        max: 12,
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="showAllVisuals"
                  control={control}
                  defaultValue={false}
                  render={({ field: { ref, ...field } }) => (
                    <SwitchButtonWidget
                      {...field}
                      label="Display all visuals"
                      ref={ref}
                      onChange={(value) =>
                        handleComponentConfigChange(
                          props,
                          'showAllVisuals',
                          value,
                          field.onChange,
                        )
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </SidebarContent>
        </SidebarContainer>
      </Drawer>

      <ProductsDrawer
        open={openProductsDrawer}
        componentDetails={props.componentDetails}
        handleOnChange={handleMultipleChange}
        onClose={onToggleProductsDrawer}
      />
    </FormWrapper>
  );
};
